import { memo, useCallback, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import {
  deleteSingleMessage,
  deleteSnippet,
  toggleIsDeletingSnippetsPopupDialog,
  togglePinnedSnippet,
  toggleSnippetFlag,
  toggleSnippetStar,
} from "rtk-slice"

import {
  CardActions,
  IconButton,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material"
import { green, yellow } from "@mui/material/colors"

import { enqueueSnackbar } from "notistack"
import {
  ChevronUpWide,
  DeleteSolid,
  Flagged,
  PaletteDuotone,
  PushPinRed,
  PushPinSolid,
  Starred,
  UnFlagged,
  UnStarred,
} from "ui-icons"

import ConfirmDeletingWarningMessage from "../Confirmation-Messages/ConfirmDeletingWarningMessage"
import SnippetBookmarkColorDialog from "./SnippetBookmarkColorDialog"

const CardAction = ({
  isPinned,
  id,
  starred,
  flagged,
  bookmarkColor,
  handleExpandClick,
}) => {
  const dispatch = useDispatch()
  const deleteSingleMessageState = useSelector(deleteSingleMessage)
  const [
    openSnippetBookmarkColorDialog,
    setOpenSnippetBookmarkColorDialog,
  ] = useState(false)
  const [singleSnippet, setSingleSnippet] = useState(false)
  const {
    palette: { mode },
  } = useTheme()

  const handleOpenSnippetBookmarkColorDialog = useCallback(() => {
    setOpenSnippetBookmarkColorDialog((prev) => !prev)
  }, [])

  const handleDeleteSingleSnippet = useCallback(() => {
    handleExpandClick()
    dispatch(deleteSnippet(id))
    enqueueSnackbar("Snippet has been deleted!")
  }, [dispatch, handleExpandClick, id])

  const handleToggleIsDeleting = () => {
    dispatch(toggleIsDeletingSnippetsPopupDialog())
    setSingleSnippet((prev) => !prev)
  }

  const handleToggleStarredSnippet = () => {
    dispatch(toggleSnippetStar(id))
  }

  const handleToggleFlaggedSnippet = (id) => {
    dispatch(toggleSnippetFlag(id))
  }

  const handleCloseSnippetBookmarkColorDialog = useCallback(() => {
    setOpenSnippetBookmarkColorDialog((prev) => !prev)
  }, [])

  const handleTogglePinnedSnippet = () => {
    dispatch(togglePinnedSnippet(id))
  }

  return (
    <>
      <CardActions
        disableSpacing
        aria-label="Snippet card action bar"
      >
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <div onClick={handleToggleStarredSnippet}>
            {starred ? (
              <Tooltip
                title={`Remove snippet from starred collection`}
              >
                <IconButton sx={{ color: yellow.A700 }}>
                  <Starred width="24" height="24" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={`Add snippet to starred collection`}>
                <IconButton aria-label="add to favorites">
                  <UnStarred width="24" height="24" />
                </IconButton>
              </Tooltip>
            )}
          </div>

          <div onClick={() => handleToggleFlaggedSnippet(id)}>
            {flagged ? (
              <Tooltip title={`Unflag snippet`}>
                <IconButton sx={{ color: green.A700 }}>
                  <Flagged fill="#00c853" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={`Flag snippet`}>
                <IconButton aria-label="flagged">
                  <UnFlagged />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <Tooltip title="Delete snippet">
            <IconButton
              onClick={
                deleteSingleMessageState
                  ? handleToggleIsDeleting
                  : handleDeleteSingleSnippet
              }
            >
              <DeleteSolid fill="crimson" width="20" height="20" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={`${
              bookmarkColor ? "Change/Remove" : "Add"
            } snippet bookmark color`}
          >
            <IconButton
              onClick={handleOpenSnippetBookmarkColorDialog}
            >
              <PaletteDuotone fill="darkorange" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              isPinned ? "Remove from pinned snippets" : "Pin snippet"
            }
          >
            <IconButton onClick={handleTogglePinnedSnippet}>
              {isPinned ? (
                <PushPinRed width="20" height="20" />
              ) : (
                <PushPinSolid
                  fill={mode === "dark" ? "lightgrey" : "grey"}
                />
              )}
            </IconButton>
          </Tooltip>
          <IconButton onClick={handleExpandClick}>
            <ChevronUpWide
              fill={mode === "dark" ? "lightgrey" : "grey"}
            />
          </IconButton>
        </Stack>
      </CardActions>
      <SnippetBookmarkColorDialog
        openSnippetBookmarkColorDialog={
          openSnippetBookmarkColorDialog
        }
        id={id}
        bookmarkColor={bookmarkColor}
        handleCloseSnippetBookmarkColorDialog={
          handleCloseSnippetBookmarkColorDialog
        }
      />

      {deleteSingleMessageState && singleSnippet ? (
        <ConfirmDeletingWarningMessage
          type="single"
          handleDeleteSingleSnippet={handleDeleteSingleSnippet}
          setSingleSnippet={setSingleSnippet}
        />
      ) : null}
    </>
  )
}

export default memo(CardAction)
